import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Building,
  User,
  Mail,
  Shield,
  Users,
  Save,
  Lock,
  Globe,
} from 'lucide-react';
import { toast } from 'sonner';

const roleColors = {
  admin: 'bg-blue-500/10 text-blue-400 border border-blue-500/20',
  approver: 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20',
  sender: 'bg-amber-500/10 text-amber-400 border border-amber-500/20',
  vendor: 'bg-violet-500/10 text-violet-400 border border-violet-500/20',
};

export default function Settings() {
  const { api, user } = useAuth();
  const [company, setCompany] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [companyForm, setCompanyForm] = useState({
    name: '',
    address: '',
    industry: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [companyRes, usersRes] = await Promise.all([
        api().get('/companies'),
        api().get('/users'),
      ]);
      setCompany(companyRes.data);
      setTeamMembers(usersRes.data);
      setCompanyForm({
        name: companyRes.data.name || '',
        address: companyRes.data.address || '',
        industry: companyRes.data.industry || '',
      });
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCompany = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api().put('/companies', companyForm);
      toast.success('Company settings saved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="h-8 w-48 skeleton" />
        <div className="h-64 skeleton rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white font-['Manrope'] tracking-tight">
          Settings
        </h1>
        <p className="text-zinc-400 mt-1">
          Manage your company and team settings
        </p>
      </div>

      <Tabs defaultValue="company">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger
            value="company"
            className="data-[state=active]:bg-zinc-800"
            data-testid="tab-company"
          >
            <Building className="w-4 h-4 mr-2" />
            Company
          </TabsTrigger>
          <TabsTrigger
            value="team"
            className="data-[state=active]:bg-zinc-800"
            data-testid="tab-team"
          >
            <Users className="w-4 h-4 mr-2" />
            Team
          </TabsTrigger>
          <TabsTrigger
            value="security"
            className="data-[state=active]:bg-zinc-800"
            data-testid="tab-security"
          >
            <Shield className="w-4 h-4 mr-2" />
            Security
          </TabsTrigger>
        </TabsList>

        {/* Company Settings */}
        <TabsContent value="company" className="space-y-6">
          <Card className="bg-zinc-900/40 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg text-zinc-200 font-['Manrope']">
                Company Information
              </CardTitle>
              <CardDescription className="text-zinc-500">
                Update your organization's details
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveCompany} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Company Name</Label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      value={companyForm.name}
                      onChange={(e) =>
                        setCompanyForm({ ...companyForm, name: e.target.value })
                      }
                      className="bg-zinc-950 border-zinc-800 pl-10"
                      required
                      data-testid="company-name-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Address</Label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      value={companyForm.address}
                      onChange={(e) =>
                        setCompanyForm({ ...companyForm, address: e.target.value })
                      }
                      placeholder="123 Business St, City, State"
                      className="bg-zinc-950 border-zinc-800 pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Industry</Label>
                  <Input
                    value={companyForm.industry}
                    onChange={(e) =>
                      setCompanyForm({ ...companyForm, industry: e.target.value })
                    }
                    placeholder="e.g., Technology, Healthcare, Finance"
                    className="bg-zinc-950 border-zinc-800"
                  />
                </div>
                {user?.role === 'admin' && (
                  <Button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-500"
                    disabled={saving}
                    data-testid="save-company-btn"
                  >
                    {saving ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Saving...
                      </span>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                )}
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Team Settings */}
        <TabsContent value="team" className="space-y-6">
          <Card className="bg-zinc-900/40 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg text-zinc-200 font-['Manrope']">
                Team Members
              </CardTitle>
              <CardDescription className="text-zinc-500">
                Manage your team and their roles
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {teamMembers.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between p-4 rounded-lg bg-zinc-800/30 border border-zinc-800"
                    data-testid={`team-member-${member.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center">
                        <User className="w-5 h-5 text-zinc-400" />
                      </div>
                      <div>
                        <p className="font-medium text-zinc-200">
                          {member.full_name}
                        </p>
                        <p className="text-sm text-zinc-500">{member.email}</p>
                      </div>
                    </div>
                    <Badge className={roleColors[member.role]}>
                      {member.role}
                    </Badge>
                  </div>
                ))}
              </div>

              {/* Role Descriptions */}
              <div className="mt-8 p-4 rounded-lg bg-zinc-800/20 border border-zinc-800">
                <h4 className="text-sm font-medium text-zinc-300 mb-3">
                  Role Permissions
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <Badge className={roleColors.admin}>Admin</Badge>
                    <p className="text-zinc-500 mt-1">
                      Full access to all features, user management, and settings
                    </p>
                  </div>
                  <div>
                    <Badge className={roleColors.approver}>Approver</Badge>
                    <p className="text-zinc-500 mt-1">
                      Can approve/reject change requests and sign documents
                    </p>
                  </div>
                  <div>
                    <Badge className={roleColors.sender}>Sender</Badge>
                    <p className="text-zinc-500 mt-1">
                      Can create documents and send them for signature
                    </p>
                  </div>
                  <div>
                    <Badge className={roleColors.vendor}>Vendor</Badge>
                    <p className="text-zinc-500 mt-1">
                      External party, can view and sign assigned documents
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Settings */}
        <TabsContent value="security" className="space-y-6">
          <Card className="bg-zinc-900/40 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg text-zinc-200 font-['Manrope']">
                Security Settings
              </CardTitle>
              <CardDescription className="text-zinc-500">
                Configure security and compliance options
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                <div className="flex items-center gap-3 mb-2">
                  <Shield className="w-5 h-5 text-emerald-400" />
                  <span className="font-medium text-emerald-400">
                    ESIGN Act Compliant
                  </span>
                </div>
                <p className="text-sm text-zinc-400">
                  All electronic signatures on this platform comply with the U.S.
                  Electronic Signatures in Global and National Commerce Act (ESIGN)
                  and the Uniform Electronic Transactions Act (UETA).
                </p>
              </div>

              <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
                <div className="flex items-center gap-3 mb-2">
                  <Lock className="w-5 h-5 text-blue-400" />
                  <span className="font-medium text-blue-400">
                    Data Encryption
                  </span>
                </div>
                <p className="text-sm text-zinc-400">
                  All data is encrypted at rest and in transit using AES-256
                  encryption. Vendor access is secured through unique, email-locked
                  access tokens.
                </p>
              </div>

              <div className="p-4 rounded-lg bg-violet-500/5 border border-violet-500/20">
                <div className="flex items-center gap-3 mb-2">
                  <Users className="w-5 h-5 text-violet-400" />
                  <span className="font-medium text-violet-400">
                    Multi-Tenant Isolation
                  </span>
                </div>
                <p className="text-sm text-zinc-400">
                  Complete data isolation between companies. Each organization's data
                  is stored and processed separately with strict access controls.
                </p>
              </div>

              <div className="text-sm text-zinc-500">
                <p className="mb-2">Security features include:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>JWT-based authentication with secure token rotation</li>
                  <li>Role-based access control (RBAC)</li>
                  <li>Complete audit trail of all actions</li>
                  <li>Email-locked vendor document access</li>
                  <li>Secure signature storage with tamper detection</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
